import { useCallback, useEffect, useRef, useState } from 'react';

import { fetchEventSource } from '@microsoft/fetch-event-source';

import { fetchDjenStatus } from '../api/client';
import { useAuth } from './useAuth';

export interface DjenProgress {
  status: 'PENDING' | 'RUNNING' | 'COMPLETED' | 'FAILED' | 'idle';
  pct?: number;
  comunicacoes?: number;
  processos?: number;
  total_comunicacoes?: number;
  error?: string;
}

const MAX_RETRIES = 5;
const POLLING_INTERVAL_MS = 10_000;

function backoffMs(attempt: number): number {
  return Math.min(2 ** attempt * 2000, 30_000);
}

/**
 * Hook SSE que acompanha o progresso da ingestão DJEN em tempo real.
 *
 * Analogia Angular: equivale a um service com EventSource que emite
 * valores via Observable (BehaviorSubject). Aqui usamos useState + useEffect.
 *
 * Inclui retry com backoff exponencial (máx 5 tentativas) e fallback
 * para polling via GET /djen/ingest/status caso o SSE falhe.
 *
 * @param enabled - Se true, conecta ao stream SSE. Se false, desconecta.
 * @returns O estado atual do progresso ou null se inativo/desconectado.
 */
export function useDjenProgress(enabled: boolean): DjenProgress | null {
  const [progress, setProgress] = useState<DjenProgress | null>(null);
  const { getToken } = useAuth();
  const retryCountRef = useRef(0);
  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  const stopPolling = useCallback(() => {
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
  }, []);

  const startPollingFallback = useCallback(() => {
    stopPolling();
    pollingRef.current = setInterval(async () => {
      try {
        const data = await fetchDjenStatus();
        const s = (data.status ?? 'idle').toUpperCase();
        if (s === 'IDLE') {
          setProgress(null);
        } else {
          setProgress({ ...data, status: s } as DjenProgress);
        }
        if (s === 'COMPLETED' || s === 'FAILED') {
          stopPolling();
        }
      } catch {
        // Erro no polling — será retentado no próximo intervalo
      }
    }, POLLING_INTERVAL_MS);
  }, [stopPolling]);

  useEffect(() => {
    if (!enabled) {
      stopPolling();
      return;
    }

    let cancelled = false;
    abortRef.current = new AbortController();

    const connect = async () => {
      if (cancelled) return;

      try {
        const token = await getToken();
        const baseUrl = import.meta.env.VITE_API_BASE_URL || '/api/v1';

        await fetchEventSource(`${baseUrl}/djen/ingest/stream`, {
          signal: abortRef.current!.signal,
          headers: { Authorization: `Bearer ${token}` },
          onmessage(ev) {
            // Mensagem recebida — resetar contador de retry
            retryCountRef.current = 0;
            try {
              const data = JSON.parse(ev.data);
              switch (ev.event) {
                case 'pending':
                  setProgress({ ...data, status: 'PENDING' });
                  break;
                case 'running':
                  setProgress({ ...data, status: 'RUNNING' });
                  break;
                case 'completed':
                  setProgress({ ...data, status: 'COMPLETED' });
                  abortRef.current?.abort();
                  break;
                case 'failed':
                  setProgress({ ...data, status: 'FAILED' });
                  abortRef.current?.abort();
                  break;
                case 'idle':
                  setProgress(null);
                  abortRef.current?.abort();
                  break;
              }
            } catch {
              // Payload inválido — ignorar evento
            }
          },
          onerror() {
            retryCountRef.current += 1;

            if (retryCountRef.current > MAX_RETRIES) {
              // SSE esgotou retries — cair para polling
              abortRef.current?.abort();
              startPollingFallback();
              return;
            }

            // Lançar para cancelar a reconexão automática da lib
            // e fazer retry manual com backoff no catch abaixo
            throw new Error('retry');
          },
        });
      } catch {
        // Conexão fechada, abortada ou erro de retry
        if (cancelled) return;

        if (retryCountRef.current <= MAX_RETRIES) {
          const delay = backoffMs(retryCountRef.current);
          setTimeout(() => {
            if (!cancelled) {
              abortRef.current = new AbortController();
              connect();
            }
          }, delay);
        } else {
          startPollingFallback();
        }
      }
    };

    connect();

    return () => {
      cancelled = true;
      abortRef.current?.abort();
      abortRef.current = null;
      stopPolling();
    };
  }, [enabled, getToken, startPollingFallback, stopPolling]);

  return progress;
}
